import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { deepEquals } from '@jack-henry/frontend-utils/functions';
import { DeepReactive } from '@jack-henry/frontend-utils/lit';
import { CompanyAccount } from '@treasury/domain/company/company-account.entity';
import { StopPaymentsService } from '@treasury/domain/stop-payments/stop-payments.service';
import { TmBaseComponent } from '@treasury/presentation';
import { type SelectItemVm, type TmValueChanged } from '@treasury/presentation/components/forms';
import '@treasury/presentation/components/layout/tm-section';
import '@treasury/presentation/components/tm-bottom-sheet';
import {
    type CurrencyRange,
    CurrencyRangeType,
} from '@treasury/presentation/components/tm-currency-range';
import { type DateRange, DateRangeType } from '@treasury/presentation/components/tm-date-range';
import { ButtonConfig } from '@treasury/presentation/components/tm-footer.types';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { mapAccountToVm } from '../../reports/data/report-filters.vm';
import { StopPaymentStatus, stopPaymentStatuses } from '../data/stop-payment-status-items';

const defaultAmountRange: CurrencyRange = {
    rangeType: CurrencyRangeType.AllAmounts,
};

const defaultDateRange: DateRange = {
    rangeType: DateRangeType.SpecificDate,
    startDate: undefined,
    endDate: undefined,
};

const defaultState = {
    accounts: true,
    status: true,
    checkAmount: true,
    checkDate: true,
    createdDate: true,
};

export const tagName = 'stop-payment-filter-sheet';
@customElement(tagName)
export class StopPaymentFilterSheet extends TmBaseComponent {
    @property({ type: Boolean })
    public open = false;

    @InjectProperty()
    declare private stopPaymentsService: StopPaymentsService;

    @DeepReactive()
    private validState = defaultState;

    @state()
    private filterAccounts: CompanyAccount[] = [];

    @state()
    private filterAccountsSelected: CompanyAccount[] = [];

    @state()
    private statusItems = [...stopPaymentStatuses];

    @state()
    private statusItemsSelected = [...this.statusItems];

    @state()
    private checkNumber?: string;

    @state()
    private checkNumberStart?: string;

    @state()
    private checkNumberEnd?: string;

    @state()
    private checkNumberType = 'All';

    @state()
    private checkNumberTypeOptions: SelectItemVm<string>[] = [
        { label: 'All', value: 'All' },
        { label: 'Specific Check Number', value: 'Specific Check Number' },
        { label: 'Check Range', value: 'Check Range' },
    ];

    @state()
    private checkAmountRange: CurrencyRange = defaultAmountRange;

    @state()
    private checkDateRange: DateRange = defaultDateRange;

    @state()
    private createdDateRange: DateRange = defaultDateRange;

    @state()
    private payee?: string;

    public isDateRangeSelection = (range: DateRange) =>
        range.rangeType === DateRangeType.DateRange ||
        range.rangeType === DateRangeType.WeekToDate ||
        range.rangeType === DateRangeType.MonthToDate ||
        range.rangeType === DateRangeType.YearToDate;

    protected render() {
        return html`<tm-bottom-sheet
            .open=${this.open}
            @close=${() => {
                this.open = false;
                this.dispatchEvent(new CustomEvent('close'));
            }}
        >
            <span slot="header-center">Filter Stop Payments</span>

            <tm-section class="p-3 mb-10">${this.renderForm()}</tm-section>
            <tm-footer
                .buttonConfig=${[
                    {
                        text: 'Apply Filters',
                        onClick: () => {
                            this.onApplyFilters();
                        },
                        disabled: Object.values(this.validState).some(isValid => !isValid),
                    },
                    {
                        importance: 'secondary',
                        text: 'Reset',
                        onClick: () => {
                            this.onResetForm();
                        },
                    },
                ] as ButtonConfig[]}
            >
            </tm-footer>
        </tm-bottom-sheet>`;
    }

    protected async firstUpdated() {
        this.filterAccounts = await this.stopPaymentsService.getFilterAccounts();
        this.filterAccountsSelected = [...this.filterAccounts];
        this.dispatchEvent(
            new CustomEvent('set-filters', {
                detail: this.toDto(),
            })
        );
    }

    private onApplyFilters() {
        this.open = false;
        this.dispatchEvent(
            new CustomEvent('apply-filters', {
                detail: this.toDto(),
            })
        );
        this.dispatchEvent(new CustomEvent('close'));
    }

    private onResetForm() {
        this.filterAccountsSelected = this.filterAccounts;
        this.statusItemsSelected = this.statusItems;
        this.checkDateRange = defaultDateRange;
        this.createdDateRange = defaultDateRange;
        this.checkAmountRange = defaultAmountRange;
        this.checkNumber = undefined;
        this.checkNumberStart = undefined;
        this.checkNumberEnd = undefined;
        this.checkNumberType = 'All';
        this.payee = undefined;
    }

    private toDto() {
        return {
            accounts: this.filterAccountsSelected.map(account => account.toDto()),
            statuses: [...this.statusItemsSelected],
            checkNumberType: this.checkNumberType,
            checkNumber: this.checkNumber,
            checkNumberStart: this.checkNumberStart,
            checkNumberEnd: this.checkNumberEnd,
            checkAmountType: this.checkAmountRange.rangeType,
            checkAmount: this.checkAmountRange.specificAmount,
            checkAmountStart: this.checkAmountRange.floorAmount,
            checkAmountEnd: this.checkAmountRange.ceilAmount,
            checkDate:
                !this.isDateRangeSelection(this.checkDateRange) && this.checkDateRange.startDate
                    ? format(this.checkDateRange.startDate || new Date(), 'P')
                    : undefined,
            checkDateStart: this.isDateRangeSelection(this.checkDateRange)
                ? format(this.checkDateRange.startDate || new Date(), 'P')
                : undefined,
            checkDateEnd: this.isDateRangeSelection(this.checkDateRange)
                ? format(this.checkDateRange.endDate || new Date(), 'P')
                : undefined,
            checkDateType: this.isDateRangeSelection(this.checkDateRange)
                ? 'Date Range'
                : 'Specific Date',
            createdDate:
                !this.isDateRangeSelection(this.createdDateRange) && this.createdDateRange.startDate
                    ? format(this.createdDateRange.startDate || new Date(), 'P')
                    : undefined,
            createdDateStart: this.isDateRangeSelection(this.createdDateRange)
                ? format(this.createdDateRange.startDate || new Date(), 'P')
                : undefined,
            createdDateEnd: this.isDateRangeSelection(this.createdDateRange)
                ? format(this.createdDateRange.endDate || new Date(), 'P')
                : undefined,
            createdDateType: this.isDateRangeSelection(this.createdDateRange)
                ? 'Date Range'
                : 'Specific Date',
            payee: this.payee,
        };
    }

    private renderForm() {
        return html`<tm-form>
            <tm-multi-select
                label="Accounts"
                .allowSelectAll=${true}
                .allSelectedText=${'All Accounts'}
                .items=${this.filterAccounts}
                .initial=${[...this.filterAccountsSelected]}
                .mapItem=${mapAccountToVm}
                @value-changed=${(e: TmValueChanged<CompanyAccount[]>) => {
                    this.filterAccountsSelected = e.detail.value || [];
                    this.validState.accounts = this.filterAccountsSelected.length > 0;
                }}
            ></tm-multi-select>
            <tm-multi-select
                label="Status"
                .allowSelectAll=${true}
                .allSelectedText=${'All Statuses'}
                .items=${this.statusItems}
                .initial=${[...this.statusItemsSelected]}
                @value-changed=${(e: TmValueChanged<StopPaymentStatus[]>) => {
                    this.statusItemsSelected = e.detail.value || [];
                    this.validState.status = this.statusItemsSelected.length > 0;
                }}
            ></tm-multi-select>
            <tm-select
                label="Check Number"
                .initial=${this.checkNumberType}
                .items=${this.checkNumberTypeOptions}
                @value-changed=${(e: CustomEvent) => {
                    this.checkNumberType = e.detail.value;
                }}
            ></tm-select>
            ${this.renderCheckNumberOption()}
            <tm-currency-range
                label="Check Amount"
                .initial=${this.checkAmountRange}
                @value-changed=${(e: TmValueChanged<CurrencyRange>) => {
                    const { isValid, value } = e.detail;
                    this.validState.checkAmount = isValid;
                    if (!value) {
                        return;
                    }
                    this.checkAmountRange = value;
                }}
            ></tm-currency-range>
            <tm-date-range
                label="Check Date"
                .dateRangeOptions=${[DateRangeType.SpecificDate, DateRangeType.DateRange]}
                .initial=${this.checkDateRange}
                @value-changed=${(e: TmValueChanged<DateRange>) => {
                    const { isValid, value } = e.detail;
                    if (value && deepEquals(value, defaultDateRange)) {
                        this.validState.checkDate = true;
                    } else {
                        this.validState.checkDate = isValid;
                    }
                    if (value) {
                        this.checkDateRange = value;
                    }
                }}
                .maxDate=${new Date()}
            ></tm-date-range>
            <tm-date-range
                label="Created Date"
                .dateRangeOptions=${[DateRangeType.SpecificDate, DateRangeType.DateRange]}
                .initial=${this.createdDateRange}
                @value-changed=${(e: TmValueChanged<DateRange>) => {
                    const { isValid, value } = e.detail;
                    if (value && deepEquals(value, defaultDateRange)) {
                        this.validState.createdDate = true;
                    } else {
                        this.validState.createdDate = isValid;
                    }
                    if (value) {
                        this.createdDateRange = value;
                    }
                }}
                .maxDate=${new Date()}
            ></tm-date-range>
            <tm-text-field
                label="Payee"
                .initial=${this.payee}
                @value-changed=${(e: TmValueChanged<string>) => {
                    this.payee = e.detail.value;
                }}
            ></tm-text-field>
        </tm-form>`;
    }

    private renderCheckNumberOption() {
        switch (this.checkNumberType) {
            case 'Specific Check Number':
                this.checkNumberStart = undefined;
                this.checkNumberEnd = undefined;
                return html`<tm-text-field
                    @value-changed=${(e: TmValueChanged<string>) => {
                        this.checkNumber = e.detail.value;
                    }}
                ></tm-text-field>`;
            case 'Check Range':
                this.checkNumber = undefined;
                return html`<div class="flex justify-between items-center">
                    <tm-text-field
                        class="max-w-40"
                        @value-changed=${(e: TmValueChanged<string>) => {
                            this.checkNumberStart = e.detail.value;
                        }}
                    ></tm-text-field>
                    <div class="my-1">to</div>
                    <tm-text-field
                        class="max-w-40"
                        @value-changed=${(e: TmValueChanged<string>) => {
                            this.checkNumberEnd = e.detail.value;
                        }}
                    ></tm-text-field>
                </div>`;
            default:
                this.checkNumber = undefined;
                this.checkNumberStart = undefined;
                this.checkNumberEnd = undefined;
                return nothing;
        }
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: StopPaymentFilterSheet;
    }
}
